import { useEffect, useState } from 'react';
import { acceptAll, rejectOptional, saveConsent, readConsent } from '@/lib/consent';
import { useConsent } from '@/hooks/useConsent';
import { useAuth } from '@/hooks/useAuth';
import { logCookieChoice } from '@/lib/consentLog';

/**
 * Cookie consent banner (29 JUN 2026) · GDPR opt-in.
 *
 * Shows once until the visitor chooses; can be re-opened any time via the
 * «Настройки cookie» control (dispatches `qastudio:open-cookie-settings`, e.g.
 * from the footer or Profile). Optional categories are off by default; only
 * «Принять все» turns analytics on. Writes through lib/consent.ts.
 *
 * Hidden on the legal pages themselves so it never covers the cookie policy
 * the visitor is reading.
 */

const OPEN_SETTINGS_EVENT = 'qastudio:open-cookie-settings';

/** Imperatively open the cookie settings panel from anywhere. */
export function openCookieSettings(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT));
  }
}

function isLegalHash(): boolean {
  if (typeof window === 'undefined') return false;
  return /^#\/(terms|privacy|cookies|consent)\b/.test(window.location.hash);
}

export function CookieConsent() {
  const consent = useConsent();
  const { user, isAuthed } = useAuth();
  const userId = user?.id ?? null;
  const email = user?.email ?? null;
  // Pre-login (login / signup) the app theme shouldn't leak into the banner —
  // force the warm editorial brand look so it matches the login/landing style.
  const brand = !isAuthed;
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [onLegal, setOnLegal] = useState(isLegalHash());

  // Re-open settings on demand (footer / profile link).
  useEffect(() => {
    const open = () => {
      const current = readConsent();
      setAnalyticsOn(current?.analytics ?? false);
      setShowSettings(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  // Keep «am I on a legal page» in sync so the banner yields to those pages.
  useEffect(() => {
    const sync = () => setOnLegal(isLegalHash());
    window.addEventListener('hashchange', sync);
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('hashchange', sync);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  const decided = consent !== null;
  // Banner is visible when the visitor hasn't decided, OR they re-opened
  // settings. Never over a legal page.
  if (onLegal) return null;
  if (decided && !showSettings) return null;

  const close = () => setShowSettings(false);

  return (
    <div
      className={`cookie-banner${brand ? ' cookie-banner--brand' : ''}`}
      role="dialog"
      aria-modal="false"
      aria-label="Согласие на использование cookie"
    >
      <div className="cookie-banner__inner">
        {!showSettings ? (
          <>
            <div className="cookie-banner__text">
              <p className="cookie-banner__title">Мы используем cookie</p>
              <p>
                Необходимые технологии обеспечивают работу сервиса. Аналитические — помогают
                его улучшать и включаются только с твоего согласия. Подробнее — в{' '}
                <a href="#/cookies">Политике cookie</a> и{' '}
                <a href="#/privacy">Политике конфиденциальности</a>.
              </p>
            </div>
            <div className="cookie-banner__actions">
              <button
                type="button"
                className="cookie-btn cookie-btn--ghost"
                onClick={() => {
                  rejectOptional();
                  void logCookieChoice(userId, email, false);
                }}
              >
                Только необходимые
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn--ghost"
                onClick={() => {
                  setAnalyticsOn(readConsent()?.analytics ?? false);
                  setShowSettings(true);
                }}
              >
                Настроить
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn--primary"
                onClick={() => {
                  acceptAll();
                  void logCookieChoice(userId, email, true);
                }}
              >
                Принять все
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-banner__text">
              <p className="cookie-banner__title">Настройки cookie</p>
              <ul className="cookie-cats">
                <li className="cookie-cat">
                  <label className="cookie-cat__row">
                    <input type="checkbox" checked disabled aria-disabled="true" />
                    <span>
                      <strong>Необходимые</strong> — вход, безопасность, сохранение прогресса.
                      Всегда включены.
                    </span>
                  </label>
                </li>
                <li className="cookie-cat">
                  <label className="cookie-cat__row">
                    <input
                      type="checkbox"
                      checked={analyticsOn}
                      onChange={(e) => setAnalyticsOn(e.target.checked)}
                    />
                    <span>
                      <strong>Аналитические</strong> — помогают понять, как используется сервис,
                      чтобы его улучшать.
                    </span>
                  </label>
                </li>
              </ul>
            </div>
            <div className="cookie-banner__actions">
              <button
                type="button"
                className="cookie-btn cookie-btn--ghost"
                onClick={() => {
                  if (decided) close();
                  else {
                    rejectOptional();
                    void logCookieChoice(userId, email, false);
                  }
                }}
              >
                {decided ? 'Отмена' : 'Отклонить'}
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn--primary"
                onClick={() => {
                  saveConsent({ analytics: analyticsOn });
                  void logCookieChoice(userId, email, analyticsOn);
                  close();
                }}
              >
                Сохранить выбор
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
